import { useState } from "react";
import { Search, ChevronDown, Trophy, Globe, LayoutGrid, X, Loader2, Star } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useCached } from "@/lib/fbCache";
import { useFbFavs } from "@/lib/useFbFavs";
import { FavStar } from "@/components/football/MatchRow";

const H = 3600e3;
const Comp = ({ c, active, onPick }) => (
  <div className={`flex items-center rounded-lg transition-colors ${active ? "bg-[#E11D2A]/15" : "hover:bg-white/5"}`}>
    <button onClick={() => onPick(c)} data-testid={`ls-comp-${c.id}`} className="flex-1 min-w-0 flex items-center gap-2.5 px-2 py-2 text-left">
      <Trophy className="w-4 h-4 text-[#E11D2A] shrink-0" />
      <span className="min-w-0"><span className={`block text-sm truncate ${active ? "text-white font-semibold" : "text-slate-200"}`}>{c.name}</span>
        {c.country && <span className="block text-[11px] text-slate-500 truncate">{c.country}</span>}</span>
    </button>
    <FavStar kind="competitions" testid={`fav-ls-comp-${c.id}`} item={{ id: `ls-${c.id}`, name: c.name, country: c.country, slug: String(c.id) }} />
  </div>
);

// Lazily loads a country's (or federation's) competitions only when expanded — saves data/credits.
function Group({ g, kind, selected, onPick, q }) {
  const [open, setOpen] = useState(false);
  const key = open ? `ls-comps:${kind}:${g.id}` : null;
  const res = useCached(key, () => endpoints.lsCompetitions(kind === "fed" ? { federation_id: g.id } : { country_id: g.id }), 24 * H);
  const comps = (res.data?.competitions || []).map((c) => ({ ...c, country: g.name })).filter((c) => !q || c.name.toLowerCase().includes(q) || g.name.toLowerCase().includes(q));
  return (
    <div>
      <button onClick={() => setOpen(!open)} data-testid={`ls-group-${kind}-${g.id}`} aria-expanded={open}
        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white">
        <Globe className="w-3.5 h-3.5 text-slate-500" /><span className="flex-1 text-left truncate">{g.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="pl-3 pb-1">{res.loading && !res.data ? <Loader2 className="w-4 h-4 animate-spin text-slate-500 m-2" />
        : comps.length ? comps.map((c) => <Comp key={c.id} c={c} active={String(selected?.id) === String(c.id)} onPick={onPick} />)
        : <p className="text-[11px] text-slate-500 px-2 py-1">No competitions</p>}</div>}
    </div>
  );
}

const Section = ({ title, id, children, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-[#273042]">
      <button onClick={() => setOpen(!open)} data-testid={`ls-section-${id}`} className="w-full flex items-center justify-between px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-slate-400 hover:text-white">
        {title}<ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-2 pb-3 space-y-0.5">{children}</div>}
    </div>
  );
};

export function WorldSidebar({ ov, selected, onPick, search, setSearch, onClose }) {
  const { favs } = useFbFavs();
  const q = search.trim().toLowerCase();
  const featured = (ov.featured || []).filter((c) => !q || `${c.name} ${c.country}`.toLowerCase().includes(q));
  const countries = (ov.countries || []).filter((c) => !q || c.name.toLowerCase().includes(q));
  const favComps = favs.competitions.filter((c) => String(c.id).startsWith("ls-")).map((c) => ({ id: Number(c.slug), name: c.name, country: c.country }));
  return (
    <div className="rounded-2xl bg-[#0F131B] border border-[#273042] overflow-hidden" data-testid="world-sidebar">
      <div className="flex items-center gap-2 px-3 py-3 border-b border-[#273042]">
        <Search className="w-4 h-4 text-slate-500 shrink-0" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teams, competitions, countries…" data-testid="fb-search-input"
          className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder-slate-500 outline-none h-8" />
        {search && <button onClick={() => setSearch("")} data-testid="fb-search-clear" className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
        {onClose && <button onClick={onClose} data-testid="fb-sidebar-close" className="text-slate-400 hover:text-white ml-1"><X className="w-5 h-5" /></button>}
      </div>
      <div className="max-h-[calc(100vh-11rem)] overflow-y-auto overscroll-contain">
        <div className="px-2 py-2">
          <button onClick={() => onPick(null)} data-testid="fb-comp-all" className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm ${!selected ? "bg-[#E11D2A]/15 text-white font-semibold" : "text-slate-200 hover:bg-white/5"}`}>
            <LayoutGrid className="w-4 h-4 text-[#E11D2A]" /> Top competitions
          </button>
        </div>
        {favComps.length > 0 && <Section title={<span className="inline-flex items-center gap-1.5"><Star className="w-3.5 h-3.5" /> Favourites</span>} id="favourites" defaultOpen>
          {favComps.map((c) => <Comp key={c.id} c={c} active={String(selected?.id) === String(c.id)} onPick={onPick} />)}</Section>}
        <Section title="Top competitions" id="featured" defaultOpen>{featured.map((c) => <Comp key={c.id} c={c} active={String(selected?.id) === String(c.id)} onPick={onPick} />)}</Section>
        <Section title="International" id="federations" defaultOpen={false}>{(ov.federations || []).map((g) => <Group key={g.id} g={g} kind="fed" selected={selected} onPick={onPick} q={q} />)}</Section>
        <Section title={`Countries (${countries.length})`} id="countries" defaultOpen={!!q}>{countries.map((g) => <Group key={g.id} g={g} kind="country" selected={selected} onPick={onPick} q="" />)}</Section>
      </div>
    </div>
  );
}
